import { useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ImageHoverPreviewProps = {
  /** Image URL to enlarge on hover. Falsy → no preview (children only). */
  src?: string | null
  alt?: string
  children: ReactNode
  className?: string
}

const PREVIEW_W = 280
const PREVIEW_H = 220
const GAP = 12

function clampPos(x: number, y: number) {
  const maxX = window.innerWidth - PREVIEW_W - 8
  const maxY = window.innerHeight - PREVIEW_H - 8
  return {
    x: Math.max(8, Math.min(x, maxX)),
    y: Math.max(8, Math.min(y, maxY)),
  }
}

/**
 * Wrap a list thumbnail: hover shows a fixed enlarged preview near the thumb.
 * No src → renders children only (no hover chrome).
 */
export function ImageHoverPreview({
  src,
  alt = '',
  children,
  className = '',
}: ImageHoverPreviewProps) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const wrapperClass = src ? `inline-flex ${className}`.trim() : className || undefined

  if (!src) {
    return <span className={wrapperClass}>{children}</span>
  }

  return (
    <span
      className={wrapperClass}
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        setPos(clampPos(rect.right + GAP, rect.top - 8))
      }}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: pos.x,
              top: pos.y,
              width: PREVIEW_W,
              height: PREVIEW_H,
              zIndex: 9999999,
            }}
            className="pointer-events-none overflow-hidden rounded border border-slate-300 bg-white p-2 shadow-lg"
            role="presentation"
          >
            <img
              src={src}
              alt={alt}
              className="h-full w-full object-contain"
            />
          </div>,
          document.body,
        )}
    </span>
  )
}
